// ============================================================================
// الملف: src/components/Layout/Sidebar.tsx
// المسؤولية: شريط التنقل الجانبي للتطبيق - يظهر في جميع صفحات الأدمن
// يحتوي على روابط لـ: الرئيسية، المستخدمين، المعاملات، المحتوى القانوني، الإعدادات
// ============================================================================

import React from 'react';
import {NavLink, useNavigate} from 'react-router-dom';
import {
    LayoutDashboard,
    Users,
    CreditCard,
    Settings,
    LogOut,
    FileText,
    Smartphone,
    X
} from 'lucide-react';
import {logout} from '../../services/api';

/**
 * خصائص مكون الشريط الجانبي
 */
interface SidebarProps {
    /** حالة فتح الشريط (للموبايل) */
    isOpen?: boolean;
    /** دالة لإغلاق الشريط (للموبايل) */
    onClose?: () => void;
    /** تحديد ما إذا كان الشريط في وضع الموبايل */
    isMobile?: boolean;
}

/**
 * مكون الشريط الجانبي
 * @param props - خصائص المكون
 * @returns JSX للشريط الجانبي
 * 
 * @example
 * // في صفحة الأدمن (ديسكتوب)
 * <Sidebar isMobile={false} />
 * 
 * @example
 * // في صفحة الأدمن (موبايل)
 * <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isMobile={true} />
 */
const Sidebar: React.FC<SidebarProps> = ({isOpen, onClose, isMobile = false}) => {
    const navigate = useNavigate();

    // ============================================================================
    // عناصر القائمة الرئيسية
    // ============================================================================
    const menuItems = [
        {path: '/dashboard', name: 'الرئيسية', icon: LayoutDashboard},
        {path: '/users', name: 'المستخدمين', icon: Users},
        {path: '/transactions', name: 'المعاملات', icon: CreditCard},
        {path: '/admin/legal', name: 'المحتوى القانوني', icon: FileText}, // <-- رابط جديد
        {path: '/settings', name: 'الإعدادات', icon: Settings},
    ];

    // ============================================================================
    // معالجة تسجيل الخروج
    // ============================================================================
    const handleLogout = () => {
        logout(); // حذف التوكن من localStorage
        navigate('/login'); // التوجيه إلى صفحة تسجيل الدخول
    };

    // ============================================================================
    // نسخة سطح المكتب (ديسكتوب) - تظهر بشكل دائم
    // ============================================================================
    if (!isMobile) {
        return (
            <div className="fixed right-0 top-0 h-screen w-64 bg-gray-900 text-white shadow-xl hidden lg:block">
                {/* رأس الشريط - اسم التطبيق */}
                <div className="p-6">
                    <h2 className="text-xl font-bold text-blue-400">CRDT by SMS</h2>
                </div>

                {/* القائمة الرئيسية */}
                <nav className="mt-6">
                    {menuItems.map(item => (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            className={({isActive}) =>
                                `flex items-center px-6 py-3 text-gray-300 hover:bg-gray-800 transition-colors ${isActive ? 'bg-gray-800 text-white border-r-4 border-blue-500' : ''
                                }`
                            }
                        >
                            <item.icon className="w-5 h-5 ml-3" />
                            {item.name}
                        </NavLink>
                    ))}

                    {/* زر تسجيل الخروج */}
                    <button
                        onClick={handleLogout}
                        className="flex items-center px-6 py-3 text-gray-300 hover:bg-gray-800 w-full text-right mt-10 border-t border-gray-700 transition-colors"
                    >
                        <LogOut className="w-5 h-5 ml-3" />
                        تسجيل خروج
                    </button>
                </nav>
            </div>
        );
    }

    // ============================================================================
    // نسخة الموبايل - تظهر عند الطلب مع خلفية معتمة
    // ============================================================================
    return (
        <>
            {/* خلفية معتمة عند فتح الشريط */}
            {isOpen && (
                <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={onClose} />
            )}

            {/* الشريط الجانبي للموبايل */}
            <div className={`fixed right-0 top-0 h-screen w-64 bg-gray-900 text-white shadow-xl z-50 transform transition-transform lg:hidden ${isOpen ? 'translate-x-0' : 'translate-x-full'
                }`}>
                {/* زر الإغلاق */}
                <button
                    onClick={onClose}
                    className="absolute left-4 top-4 text-gray-400 hover:text-white transition-colors"
                >
                    <X className="w-5 h-5" />
                </button>

                {/* رأس الشريط - اسم التطبيق */}
                <div className="p-6">
                    <h2 className="text-xl font-bold text-blue-400">CRDT by SMS</h2>
                </div>

                {/* القائمة الرئيسية للموبايل */}
                <nav className="mt-6">
                    {menuItems.map(item => (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            onClick={onClose} // إغلاق الشريط بعد النقر
                            className={({isActive}) =>
                                `flex items-center px-6 py-3 text-gray-300 hover:bg-gray-800 transition-colors ${isActive ? 'bg-gray-800 text-white border-r-4 border-blue-500' : ''
                                }`
                            }
                        >
                            <item.icon className="w-5 h-5 ml-3" />
                            {item.name}
                        </NavLink>
                    ))}

                    {/* زر تسجيل الخروج للموبايل */}
                    <button
                        onClick={handleLogout}
                        className="flex items-center px-6 py-3 text-gray-300 hover:bg-gray-800 w-full text-right mt-10 border-t border-gray-700 transition-colors"
                    >
                        <LogOut className="w-5 h-5 ml-3" />
                        تسجيل خروج
                    </button>
                </nav>
            </div>
        </>
    );
};

export default Sidebar;